import { auth } from '@/lib/firebase';
import { toast } from '@/components/ui/use-toast';

// — Constants ———————————————————————————————————————————————
export interface ScanQuota {
  remainingScans: number;
  isSubscribed: boolean;
  isGuest?: boolean;
  subscriptionTier?: string | null;
  maxScans?: number;
  usedScans?: number;
}

const CACHE_TTL            = 60_000;  // 1 minute
const GUEST_SCAN_LIMIT     = 2;       // matches backend
const USER_FREE_SCANS      = 2;       // matches backend
const TOKEN_REFRESH_COOLDOWN = 10_000; // 10s
const MAX_FAILED_ATTEMPTS  = 3;

// Point at your deployed function (and to the emulator locally)
const SCAN_QUOTA_API = 'https://getscanquota-f2cz2uwarq-uc.a.run.app';

export const scanQuotaService = {
  _cache: null as ScanQuota | null,
  _lastFetch: 0,
  _lastTokenRefresh: 0,
  _failedAttempts: 0,
  _circuitBroken: false,
  _circuitRecoveryTime: 0,

  // Circuit-breaker reset
  _checkCircuitBreaker() {
    if (this._circuitBroken && Date.now() > this._circuitRecoveryTime) {
      this._circuitBroken   = false;
      this._failedAttempts = 0;
      console.log('scanQuotaService: circuit breaker reset');
    }
  },

  // Rate-limited token fetch
  async _getAuthToken(): Promise<string|null> {
    const user = auth.currentUser;
    if (!user) return null;

    this._checkCircuitBreaker();
    if (this._circuitBroken) return null;

    try {
      const now = Date.now();
      if (now - this._lastTokenRefresh < TOKEN_REFRESH_COOLDOWN) {
        // use cached
        return await user.getIdToken(false);
      }
      this._lastTokenRefresh = now;
      return await user.getIdToken(true);
    } catch (err: any) {
      // handle Firebase token quota errors
      if (err.code === 'auth/quota-exceeded') {
        this._failedAttempts++;
        if (this._failedAttempts >= MAX_FAILED_ATTEMPTS) {
          this._circuitBroken      = true;
          this._circuitRecoveryTime = Date.now() + 60_000; // 1 min
          toast({
            title: "Authentication Error",
            description: "Too many token requests — please wait a minute.",
            variant: "destructive",
          });
        }
      }
      console.error('scanQuotaService: _getAuthToken error', err);
      return null;
    }
  },

  // Parse JSON or throw with the HTML snippet
  async _safeParseJSON(response: Response): Promise<any> {
    const ct = response.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      return response.json();
    }
    const text = await response.text();
    console.error('scanQuotaService: Expected JSON, got HTML:', text.slice(0,200));
    if (text.includes('Firebase Authentication')) {
      throw new Error('Authentication error — please sign in again');
    }
    throw new Error('Invalid response format: Expected JSON');
  },

  // Validate the shape
  _isValidQuotaResponse(obj: any): obj is ScanQuota {
    return (
      obj &&
      typeof obj.remainingScans === 'number' &&
      typeof obj.isSubscribed === 'boolean'
    );
  },

  // Public: fetch or return cached
  async getQuota(forceRefresh = false): Promise<ScanQuota> {
    const now = Date.now();
    // Cache hit?
    if (!forceRefresh && this._cache && now - this._lastFetch < CACHE_TTL) {
      return this._cache;
    }

    this._checkCircuitBreaker();
    if (this._circuitBroken) {
      console.warn('scanQuotaService: circuit breaker active — returning fallback');
      return { remainingScans: USER_FREE_SCANS, isSubscribed: false };
    }

    try {
      // Build headers
      const headers: Record<string,string> = {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      };

      // Attach auth if present
      const token = await this._getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;

      console.log('scanQuotaService: fetching quota from', SCAN_QUOTA_API);
      const res = await fetch(SCAN_QUOTA_API, {
        method: 'GET',
        headers,
        cache: 'no-cache',
        credentials: 'same-origin',
      });

      if (!res.ok) {
        console.error(`scanQuotaService: HTTP ${res.status}`, await res.text());
        throw new Error(`HTTP error ${res.status}`);
      }

      const json = await this._safeParseJSON(res);
      if (!this._isValidQuotaResponse(json)) {
        console.error('scanQuotaService: invalid quota JSON', json);
        throw new Error('Invalid JSON structure');
      }

      // Success!
      this._cache     = json;
      this._lastFetch = now;
      this._failedAttempts = 0;
      this._circuitBroken  = false;
      return json;

    } catch (err: any) {
      console.error('scanQuotaService: getQuota failed', err);

      // Return cached if available
      if (this._cache) {
        return this._cache;
      }

      // Guest fallback
      if (!auth.currentUser) {
        return {
          remainingScans: GUEST_SCAN_LIMIT,
          isSubscribed: false,
          isGuest: true,
          maxScans: GUEST_SCAN_LIMIT
        };
      }

      // Authenticated fallback
      return {
        remainingScans: USER_FREE_SCANS,
        isSubscribed: false
      };
    }
  },

  async hasAvailableScans(): Promise<boolean> {
    const q = await this.getQuota();
    return q.isSubscribed || q.remainingScans > 0;
  },

  invalidateCache() {
    this._cache     = null;
    this._lastFetch = 0;
  }
}; 